import React, { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import CardHeader from '../../components/CardHeader';
import UpInput from '../../components/Common/UpInput';
import UpButton from '../../components/Common/UpButton';
import UpLoadingBar from '../../components/Common/UpLoadingBar';
import UpLoadingCover from '../../components/Common/UpLoadingCover';
import UpToast from '../../components/Common/UpToast';
import { acceptInviteAccess, verifyAccessInviteOtp } from '../../api/auth';
import { checkErrorTypeString } from '../../helpers/utils';
import LocalStorageHelper from '../../helpers/localStorageHelpers';

function AuthAccessInvite() {
  const [otp, setOtp] = useState('');
  const [inputError, setInputError] = useState('');
  const [resendCount, setResendCount] = useState(30);
  const [startCounter, setStartCounter] = useState(true);
  const { selectedLogin, isMobile, emailMobile, countryCode } =
    LocalStorageHelper.getUserDetails();

  useEffect(() => {
    if (startCounter && resendCount > 0) {
      setTimeout(() => setResendCount(resendCount - 1), 1000);
    }

    if (resendCount === 0) {
      setStartCounter(false);
      setResendCount(30);
    }
  }, [resendCount, startCounter]);

  const { isLoading: verifyLoading, mutate: callVerifyInviteOtp } = useMutation(
    {
      mutationFn: ({ token, params }) => verifyAccessInviteOtp(token, params),
      onSuccess: resp => {
        window.location.href = resp.data.magic_link;
      },
      onError: error => {
        const errorMessage = checkErrorTypeString(
          error?.response?.data?.message,
        );
        setInputError(errorMessage);
      },
    },
  );

  const { isLoading: acceptLoading, mutate: callAcceptInviteAccess } =
    useMutation({
      mutationFn: acceptInviteAccess,
      onSuccess: response => {
        setStartCounter(true);
        UpToast.success('OTP sent successfully.');
        if (response?.access_token) {
          LocalStorageHelper.setAccessToken(response?.access_token);
        }
      },
      onError: error => {
        const errorMessage = checkErrorTypeString(
          error?.response?.data?.message,
        );
        UpToast.error(errorMessage);
      },
    });

  async function handleSubmit(e) {
    e.preventDefault();

    if (!otp) return;

    const params = {
      otp,
      ...(isMobile
        ? { phone: emailMobile, isd_code: countryCode }
        : { email: emailMobile }),
    };
    callVerifyInviteOtp({ token: LocalStorageHelper.getAccessToken(), params });
  }

  async function onResendOtp() {
    setOtp('');
    setInputError('');

    const params = {
      ...(isMobile
        ? { phone: emailMobile, isd_code: countryCode }
        : { email: emailMobile }),
    };
    callAcceptInviteAccess(params);
  }

  function onOtpChange(updatedValue) {
    setInputError('');
    setOtp(updatedValue);
  }

  return (
    <div className="mb-8">
      <UpLoadingBar loading={verifyLoading || acceptLoading} />
      <div className="light-view-wrapper relative">
        <UpLoadingCover loading={verifyLoading || acceptLoading} />
        <div className="flex-grow">
          <CardHeader title="Authentication Required" />
          <div className="flex items-center mt-3">
            <div className="text-sm text-up-clr-dark-grey mr-1">
              OTP sent to <span className="font-semibold">{selectedLogin}</span>
            </div>
          </div>
          <form onSubmit={handleSubmit}>
            <UpInput
              type="number"
              name="otp"
              label="Enter OTP"
              placeholder="xxx-xxx"
              wrapperClass="mt-12 mb-8"
              loading={verifyLoading || acceptLoading}
              value={otp}
              onChange={onOtpChange}
              errorContent={inputError}
              extraAction={
                startCounter ? (
                  <div className="text-up-clr-dark-grey font-semibold text-sm text-right">
                    Resend OTP in 00:{('0' + resendCount).slice(-2)}
                  </div>
                ) : (
                  <UpButton
                    variant="link"
                    onClick={onResendOtp}
                    wrapperClass="w-max ml-auto">
                    Resend OTP
                  </UpButton>
                )
              }
            />
            <UpButton
              disabled={!otp}
              type="submit"
              loading={verifyLoading || acceptLoading}>
              Verify
            </UpButton>
          </form>
        </div>
      </div>
    </div>
  );
}

export default AuthAccessInvite;
